
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import EventDetailsForm from "@/components/booking/EventDetailsForm";
import DesignServiceForm from "@/components/booking/DesignServiceForm";
import PricingSummary from "@/components/booking/PricingSummary";
import { calculatePrice } from "@/utils/pricing";

const Book = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(true); // For demo purposes
  
  // Redirecting unauthenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/auth");
    }
  }, [isAuthenticated, navigate]);

  // Event details state
  const [eventName, setEventName] = useState("");
  const [eventType, setEventType] = useState("");
  const [eventDate, setEventDate] = useState<Date | undefined>();
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [guestCount, setGuestCount] = useState(15); // Default party size
  const [printQuantity, setPrintQuantity] = useState(20); // Default print quantity
  const [eventDescription, setEventDescription] = useState("");
  
  // Design and service preferences state
  const [designDetails, setDesignDetails] = useState("");
  const [additionalRequests, setAdditionalRequests] = useState("");
  const [tshirtPrinting, setTshirtPrinting] = useState(true); // Default to true since standard package is default
  const [photoBooth, setPhotoBooth] = useState(false);
  const [music, setMusic] = useState(false);
  const [posters, setPosters] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("standard");
  
  const price = calculatePrice(
    selectedPackage,
    tshirtPrinting,
    photoBooth,
    music,
    posters,
    guestCount,
    printQuantity
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Booking Request Submitted",
      description: "We'll get back to you soon to confirm your booking!",
    });
  };

  if (!isAuthenticated) {
    return <div>Loading...</div>;
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4 text-center">
            Book Your{" "}
            <span className="bg-gradient-to-r from-madts-purple to-madts-pink bg-clip-text text-transparent">
              Custom Event
            </span>
          </h1>
          <p className="text-lg text-center text-gray-600 mb-6 max-w-3xl mx-auto">
            Let us bring your vision to life with custom apparel and designs for any occasion.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
              <CardDescription>
                Tell us about your event and party needs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EventDetailsForm 
                eventName={eventName}
                setEventName={setEventName}
                eventType={eventType}
                setEventType={setEventType}
                eventDate={eventDate}
                setEventDate={setEventDate}
                startTime={startTime}
                setStartTime={setStartTime}
                endTime={endTime}
                setEndTime={setEndTime}
                guestCount={guestCount}
                setGuestCount={setGuestCount}
                printQuantity={printQuantity}
                setPrintQuantity={setPrintQuantity}
                eventDescription={eventDescription}
                setEventDescription={setEventDescription}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Design & Service Preferences</CardTitle>
              <CardDescription>
                Share your design ideas and service preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DesignServiceForm 
                designDetails={designDetails}
                setDesignDetails={setDesignDetails}
                additionalRequests={additionalRequests}
                setAdditionalRequests={setAdditionalRequests}
                selectedPackage={selectedPackage}
                setSelectedPackage={setSelectedPackage}
                tshirtPrinting={tshirtPrinting}
                setTshirtPrinting={setTshirtPrinting}
                photoBooth={photoBooth}
                setPhotoBooth={setPhotoBooth}
                music={music}
                setMusic={setMusic}
                posters={posters}
                setPosters={setPosters}
                printQuantity={printQuantity}
              />
            </CardContent>
          </Card>
        </div>

        <PricingSummary 
          price={price}
          tshirtPrinting={tshirtPrinting}
          printQuantity={printQuantity}
          handleSubmit={handleSubmit}
        />
      </div>
    </MainLayout>
  );
};

export default Book;
